import React, { useState, useMemo, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { saveAs } from 'file-saver'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ListModule from '../module/ListModule'
import UploadFileDialog from './UploadFileDialog'
import PreviewFileDialog from './PreviewFileDialog'
import usePDF from '../hooks/usePDF'
import { dateOperators, textOperators } from '../../utils/constants/operators'
import { bufferToBlob } from '../../utils/documentManager'

function FilesDialog({
  open,
  onClose,
  setObject,
  setParentObject,
  parentObject,
  id: idProp,
  objectType: objectProp,
  ...other
}) {
  const [files, setFiles] = useState([])
  const [refresh, setRefresh] = useState(false)
  const [openUpload, setOpenUpload] = useState(false)
  const [openPreview, setOpenPreview] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [selectedFile, setSelectedFile] = useState({ id: null, action: null })

  const userRight = useOutletContext()

  const pdfPreviewUrl = usePDF(previewFile)

  useEffect(() => {
    if (refresh) setRefresh(false)
  }, [refresh])

  useEffect(() => {
    const { id, action } = selectedFile
    if (!id || !action) return

    const fileObj = files.find((f) => f.id === id)
    if (!fileObj || !fileObj.file) {
      setSelectedFile({ id: null, action: null })
      return
    }

    try {
      const blob = fileObj.file instanceof Blob ? fileObj.file : bufferToBlob(fileObj.file)

      if (action === 'preview') {
        if (blob.type === 'application/pdf' && blob.size > 0) {
          setPreviewFile(blob)
          setOpenPreview(true)
        } else {
        }
      } else if (action === 'download') {
        saveAs(blob, fileObj.filename)
      }
    } catch (error) {
    } finally {
      setSelectedFile({ id: null, action: null })
    }
  }, [selectedFile, files])

  const handleClose = () => onClose && onClose()

  const columns = useMemo(
    () => [
      { name: 'filename', label: 'Nom', isFilter: true, operators: textOperators },
      { name: 'createdBy', label: 'Créé par', isFilter: true, operators: textOperators },
      {
        name: 'createdDateTime',
        label: 'Date création',
        isFilter: true,
        inputType: 'datetime-local',
        type: 'date',
        format: 'DD/MM/YYYY HH:mm',
        operators: dateOperators
      },
      { name: 'lastModBy', label: 'Modifié par', isFilter: true, operators: textOperators },
      {
        name: 'lastModDateTime',
        label: 'Date modification',
        isFilter: true,
        inputType: 'datetime-local',
        type: 'date',
        format: 'DD/MM/YYYY HH:mm',
        operators: dateOperators
      }
    ],
    []
  )

  const options = useMemo(
    () => ({
      tableStyle: { maxHeight: 350, padding: '1px' },
      returnData: true,
      accessRight: { ...userRight, is_delete: true },
      paperStyle: { width: '100%' },
      objectType: 'attachments',
      actions: {
        add: true,
        delete: { message: 'Voulez-vous vraiment supprimer le fichier', field: 'filename' },
        customActions: [
          {
            title: 'Visualiser',
            onClick: (id) => setSelectedFile({ id, action: 'preview' }),
            icon: () => <VisibilityIcon />
          },
          {
            title: 'Télécharger',
            onClick: (id) => setSelectedFile({ id, action: 'download' }),
            icon: () => <FileDownloadIcon />
          }
        ]
      },
      objectFilter: () => ({ parentType: objectProp, parentId: idProp }),
      isFilter: true,
      columns
    }),
    [userRight, objectProp, idProp, columns]
  )

  const toolbar = useMemo(
    () => ({
      isFilter: true,
      columns: options.columns,
      style: { marginTop: '5px', marginBottom: '5px' },
      customActionsRight: [
        { icon: () => <FileUploadIcon />, tooltip: 'Ajouter fichier', onClick: () => setOpenUpload(true) }
      ]
    }),
    [options.columns]
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      aria-labelledby='files-dialog'
      sx={{
        '& .MuiDialog-paper': {
          width: '800px',
          maxHeight: '600px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      {...other}
    >
      <DialogTitle>Pièces jointes</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            height: 400,
            display: 'flex'
          }}
        >
          <ListModule
            setReturnData={setFiles}
            options={options}
            toolbar={toolbar}
            refresh={refresh}
            setParentObject={setParentObject}
            parentObject={parentObject}
          />
          <UploadFileDialog
            setRefresh={setRefresh}
            setObject={setObject}
            objectType={objectProp}
            files={files}
            id={idProp}
            open={openUpload}
            onClose={() => setOpenUpload(false)}
          />
          <PreviewFileDialog open={openPreview} onClose={() => setOpenPreview(false)} previewFile={pdfPreviewUrl} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  )
}

export default FilesDialog
